/**
 * Fiche d'un participant : profil, mise en relation, prise de rendez-vous.
 *
 * La proposition de rendez-vous se fait en trois gestes : durée → créneau →
 * confirmer. Les créneaux affichés sont déjà l'intersection des disponibilités
 * des deux personnes, moins leurs rendez-vous — proposer un créneau impossible
 * est une impasse que l'interface ne doit jamais ouvrir.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { appelerApi, envoyer } from '../reseau.ts';
import { useSession } from '../etat.tsx';
import { Avatar, nomAffiche } from '../composants/FicheParticipant.tsx';

type Fiche = {
  participant_id: string;
  prenom: string | null;
  nom: string | null;
  entreprise: string | null;
  fonction: string | null;
  secteur: string | null;
  besoins: string | null;
  objectifs: string[];
  bio: string | null;
  site_web: string | null;
  linkedin: string | null;
  present: boolean;
  en_relation: boolean;
  email: string | null;
  telephone: string | null;
  coordonnees_partagees: boolean;
};

type Creneau = { debut: string; fin: string };

const heure = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });

export function FicheDetail() {
  const { participantId } = useParams<{ participantId: string }>();
  const naviguer = useNavigate();
  const { moi } = useSession();
  const [fiche, setFiche] = useState<Fiche | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Prise de rendez-vous
  const [duree, setDuree] = useState<number | null>(null);
  const [creneaux, setCreneaux] = useState<Creneau[] | null>(null);
  const [speed, setSpeed] = useState(false);
  const [creneauChoisi, setCreneauChoisi] = useState<Creneau | null>(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!participantId) return;
    void appelerApi<{ fiche: Fiche }>(`/api/repertoire/participants/${participantId}`).then((r) => {
      if (r.donnees) setFiche(r.donnees.fiche);
      else setErreur(r.erreur);
    });
  }, [participantId]);

  const chargerCreneaux = async (d: number) => {
    setDuree(d);
    setCreneauChoisi(null);
    setCreneaux(null);
    const r = await appelerApi<{ creneaux: Creneau[] }>(
      `/api/rendez-vous/creneaux/${participantId}?duree=${d}`,
    );
    setCreneaux(r.donnees?.creneaux ?? []);
  };

  // Speed Match : cinq minutes, sur ce qu'il reste de la journée, sans avoir à
  // remplir un agenda. C'est le format du jour J.
  const chargerSpeedMatch = async () => {
    setDuree(5);
    setCreneauChoisi(null);
    setCreneaux(null);
    setSpeed(true);
    const r = await appelerApi<{ creneaux: Creneau[] }>(`/api/rendez-vous/speed-match/${participantId}`);
    setCreneaux(r.donnees?.creneaux ?? []);
  };

  const proposer = async () => {
    if (!creneauChoisi || !duree) return;
    setEnvoi(true);
    const r = await envoyer(`/api/rendez-vous/`, {
      inviteId: participantId,
      debut: creneauChoisi.debut,
      dureeMin: duree,
      speedMatch: speed,
    });
    setEnvoi(false);
    if (r.erreur) {
      setMessage(r.erreur);
      void chargerCreneaux(duree); // le créneau vient peut-être d'être pris
      return;
    }
    naviguer('/agenda');
  };

  const suivre = async () => {
    const r = await envoyer('/api/reseau/connexions', {
      cibleId: participantId,
      type: 'contact_echange',
    });
    setMessage(r.erreur ?? 'Demande de mise en relation enregistrée.');
    if (!r.erreur && participantId) {
      const rf = await appelerApi<{ fiche: Fiche }>(`/api/repertoire/participants/${participantId}`);
      if (rf.donnees) setFiche(rf.donnees.fiche);
    }
  };

  if (erreur) return <p className="message-erreur">{erreur}</p>;
  if (!fiche) return <p>Chargement…</p>;

  return (
    <div className="fiche-detail">
      <header className="fiche-detail__entete">
        <Avatar p={fiche} />
        <div>
          <h1 className="fiche-detail__nom">{nomAffiche(fiche)}</h1>
          <p className="fiche-detail__role">
            {[fiche.fonction, fiche.entreprise].filter(Boolean).join(' · ')}
          </p>
          <p className="fiche-detail__meta">
            {fiche.secteur && <span className="etiquette">{fiche.secteur}</span>}
            {fiche.present && <span className="etiquette etiquette--succes">Sur place</span>}
          </p>
        </div>
      </header>

      {fiche.besoins && (
        <section className="carte fiche-detail__besoins">
          <h2>Ce qu'il ou elle cherche</h2>
          <p>{fiche.besoins}</p>
        </section>
      )}

      {fiche.bio && <p className="fiche-detail__bio">{fiche.bio}</p>}

      {fiche.coordonnees_partagees && (
        <section className="carte fiche-detail__coordonnees">
          <h2>Coordonnées</h2>
          {fiche.email && <p><a href={`mailto:${fiche.email}`}>{fiche.email}</a></p>}
          {fiche.telephone && <p><a href={`tel:${fiche.telephone}`}>{fiche.telephone}</a></p>}
          <a className="bouton bouton--secondaire" href={`/api/contacts/vcard/${fiche.participant_id}`}>
            Ajouter au répertoire
          </a>
        </section>
      )}

      {participantId === moi?.participant_id ? (
        <section className="carte fiche-detail__rdv">
          <h2>C’est vous</h2>
          <p className="fiche-detail__aucun-creneau">
            Voilà comment les autres participants vous voient. Complétez votre profil pour donner
            envie de vous proposer un rendez-vous.
          </p>
        </section>
      ) : (
      <section className="carte fiche-detail__rdv">
        <h2>Proposer un rendez-vous</h2>
        <p className="fiche-detail__speed">
          <button className="bouton" onClick={() => void chargerSpeedMatch()}>
            ⚡ Speed Match — 5 min, maintenant
          </button>
          <span className="fiche-detail__speed-note">
            Cinq minutes sur ce qu’il reste de la journée, sans agenda à remplir. +5 points ; +10 s’il accepte.
          </span>
        </p>
        <div className="fiche-detail__durees">
          {(moi?.durees_creneau ?? [5, 10, 15]).map((d) => (
            <button
              key={d}
              className={`puce cible${duree === d ? ' puce--active' : ''}`}
              aria-pressed={duree === d}
              onClick={() => { setSpeed(false); void chargerCreneaux(d); }}
            >
              {d} min
            </button>
          ))}
        </div>

        {creneaux !== null &&
          (creneaux.length === 0 ? (
            <p className="fiche-detail__aucun-creneau">
              {speed
                ? 'Plus de créneau libre d’ici la fin de l’événement.'
                : 'Aucun créneau commun. Essayez le Speed Match ci-dessus, ou ajoutez des disponibilités dans votre agenda.'}
            </p>
          ) : (
            <div className="fiche-detail__creneaux">
              {creneaux.map((c) => (
                <button
                  key={c.debut}
                  className={`puce cible${creneauChoisi?.debut === c.debut ? ' puce--active' : ''}`}
                  aria-pressed={creneauChoisi?.debut === c.debut}
                  onClick={() => setCreneauChoisi(c)}
                >
                  {heure(c.debut)}
                </button>
              ))}
            </div>
          ))}

        {creneauChoisi && (
          <button className="bouton" disabled={envoi} onClick={() => void proposer()}>
            {envoi ? 'Envoi…' : `Proposer ${heure(creneauChoisi.debut)} (${duree} min)`}
          </button>
        )}
      </section>
      )}

      {!fiche.en_relation && participantId !== moi?.participant_id && (
        <button className="bouton bouton--secondaire" onClick={() => void suivre()}>
          Se connecter
        </button>
      )}

      {message && <p className="message-info" role="status">{message}</p>}
    </div>
  );
}
